import React, {
    useCallback, useContext, useEffect, useMemo, useState 
} from 'react';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

type Props = {
    position: number
};

export const DrinkRecommendations = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const drinksAnswer = useMemo(() => formContext.answers.totalDrinksBaseline, [formContext.answers.totalDrinksBaseline]);
    const targetDrinksAnswer = useMemo(() => formContext.answers.targetTotalDrinks, [formContext.answers.targetTotalDrinks]);
    const dryDaysAnswer = useMemo(() => formContext.answers.totalDryDaysBaseline, [formContext.answers.totalDryDaysBaseline]);
    const targetDryDaysAnswer = useMemo(() => formContext.answers.targetTotalDryDays, [formContext.answers.targetTotalDryDays]);
    const endGoalAnswer = useMemo(() => formContext.answers.endGoal, [formContext.answers.endGoal]);
    const gender = useMemo(() => formContext.answers.gender, [formContext.answers.gender]);
    const ageRange = useMemo(() => formContext.answers['age_range'], [formContext.answers['age_range']]);
    const pronouns = useMemo(() => formContext.answers.pronouns, [formContext.answers.pronouns]);
    const customTargetsAnswer = useMemo(() => formContext.answers.customTargets, [formContext.answers.customTargets]);
    const drinkBaselines = useMemo(() => {
        const answers = formContext.answers;
        return [
            answers.mon_baseline,
            answers.tue_baseline,
            answers.wed_baseline,
            answers.thu_baseline,
            answers.fri_baseline,
            answers.sat_baseline,
            answers.sun_baseline
        ];
    }, [
        formContext.answers.mon_baseline,
        formContext.answers.tue_baseline,
        formContext.answers.wed_baseline,
        formContext.answers.thu_baseline,
        formContext.answers.fri_baseline,
        formContext.answers.sat_baseline,
        formContext.answers.sun_baseline
    ]);

    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '32px'
        },
        summary: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '8px'
        },
        item: {
            display: 'grid',
            gridTemplateColumns: '32px 1fr',
            width: '100%',
            alignItems: 'center',
            opacity: 0,
            overflow: 'hidden',
            borderRadius: '32px 8px 8px 32px',
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`,
        },
        value: {
            left: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            fontWeight: TypographyWeights.BOLD,
            color: theme.palette.white,
            borderRadius: '50%',
            backgroundColor: theme.palette.warning.main,
            border: `4px solid ${theme.palette.white}`,
            zIndex: 1
        },
        text: {
            minHeight: '46px',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: theme.palette.warning['100'],
            paddingLeft: '52px',
            borderRadius: '8px',
            transform: 'translate(-100vw, 0)',
            opacity: 0,
            animation: `$shift ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.75}s, $fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`
        },
        tipBox: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '2px',
            borderRadius: '8px',
            opacity: 0,
            transform: 'translate(0, 100vh)',
            animation: `$fadeIn ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 1.25}s, $slideUp ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`
        },
        tipBodyBox: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            backgroundColor: theme.palette.warning['100'],
            padding: '16px',
            borderRadius: '8px'
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes slideUp': {
            '0%': {
                transform: 'translate(0, 100vh)' 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        },
        '@keyframes shift': {
            '0%': {
                transform: 'translate(-100vw, 0)' 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        },
    })), [skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    const calculateRecommendations = useCallback(() => {
        let body = '';
        let recommendedWeeklyDrinks = null;
        let recommendedDailyDrinks = null;
        let recommendedDryDays = null;
        let note = '';

        if (drinksAnswer === 0) {
            if (gender === 'Male' && pronouns === 'He / Him') {
                body = `For < span style = "color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};" > men ages ${ageRange}</span >, eliminating alcohol completely is the healthiest choice.We'll work together to get there at your pace.`;
            } else if (gender === 'Female' && pronouns === 'She / Her') {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">women ages ${ageRange}</span>, eliminating alcohol completely is the healthiest choice. We'll work together to get there at your pace.`;
            } else {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">people ages ${ageRange}</span>, eliminating alcohol completely is the healthiest choice. We'll work together to get there at your pace.`;
            }
            recommendedWeeklyDrinks = 0;
            recommendedDryDays = 7;
            note = 'Since you\'re already starting at 0 drinks per week, you\'ve got a head start! Our coaching team will work with you to maintain at this level.';
        } else if (customTargetsAnswer === 'true') {
            if (gender === 'Male' && pronouns === 'He / Him' && targetDrinksAnswer >= 14) {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">men ages ${ageRange}</span>, here's what the National Institutes of Health recommend for target weekly consumption:`;
                recommendedWeeklyDrinks = '<14';
                recommendedDailyDrinks = '<4';
                recommendedDryDays = '4+';
                note = `The drink target of ${targetDrinksAnswer} drinks per week you shared earlier is a bit higher than these guidelines, and that's totally fine. We'll start with your targets, and then can decide together if we want to continue tapering or not. You're in charge!`;
            } else if (gender === 'Male' && pronouns === 'He / Him' && targetDrinksAnswer < 14) {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">men ages ${ageRange}</span>, drinking less is always the healthiest choice. Here's the first set of targets we'll work towards together:`;
                recommendedWeeklyDrinks = `<${targetDrinksAnswer + 1}`;
                recommendedDailyDrinks = '<4';
                recommendedDryDays = `${targetDryDaysAnswer}${targetDryDaysAnswer === 7 ? '' : '+'}`;
                note = `The target of ${targetDrinksAnswer} drinks per week you shared earlier puts you into a moderate drinking range as defined by the National Institutes of Health. We'll get you to this goal, then we can decide to stabilize or continue to cut down from there. You're in charge!`;
            } else if (gender === 'Female' && pronouns === 'She / Her' && targetDrinksAnswer >= 8) {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">women ages ${ageRange}</span>, here's what the National Institutes of Health recommend for target weekly consumption:`;
                recommendedWeeklyDrinks = '<8';
                recommendedDailyDrinks = '<2';
                recommendedDryDays = '4+';
                note = `The drink target of ${targetDrinksAnswer} drinks per week you shared earlier is a bit higher than these guidelines, and that's totally fine. We'll start with your targets, and then can decide together if we want to continue tapering or not. You're in charge!`;
            } else if (gender === 'Female' && pronouns === 'She / Her' && targetDrinksAnswer < 8) {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">women ages ${ageRange}</span>, drinking less is always the healthiest choice. Here's the first set of targets we'll work towards together:`;
                recommendedWeeklyDrinks = `<${targetDrinksAnswer + 1}`;
                recommendedDailyDrinks = '<2';
                recommendedDryDays = `${targetDryDaysAnswer}${targetDryDaysAnswer === 7 ? '' : '+'}`;
                note = `The target of ${targetDrinksAnswer} drinks per week you shared earlier puts you into a moderate drinking range as defined by the National Institutes of Health. We'll get you to this goal, then we can decide to stabilize or continue to cut down from there. You're in charge!`;
            } else if (((gender === 'Male' && pronouns !== 'He / Him') || (gender === 'Female' && pronouns !== 'She / Her')) && targetDrinksAnswer >= 8) {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">people ages ${ageRange}</span>, here's what the National Institutes of Health recommend for target weekly consumption:`;
                recommendedWeeklyDrinks = '<8';
                recommendedDailyDrinks = '<2';
                recommendedDryDays = '4+';
                note = `The drink target of ${targetDrinksAnswer} drinks per week you shared earlier is a bit higher than these guidelines, and that's totally fine. We'll start with your targets, and then can decide together if we want to continue tapering or not. You're in charge!`;
            } else if (((gender === 'Male' && pronouns !== 'He / Him') || (gender === 'Female' && pronouns !== 'She / Her')) && targetDrinksAnswer < 8) {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">people ages ${ageRange}</span>, drinking less is always the healthiest choice. Here's the first set of targets we'll work towards together:`;
                recommendedWeeklyDrinks = `<${targetDrinksAnswer + 1}`;
                recommendedDailyDrinks = '<2';
                recommendedDryDays = `${targetDryDaysAnswer}${targetDryDaysAnswer === 7 ? '' : '+'}`;
                note = `The target of ${targetDrinksAnswer} drinks per week you shared earlier puts you into a moderate drinking range as defined by the National Institutes of Health. We'll get you to this goal, then we can decide to stabilize or continue to cut down from there. You're in charge!`;
            }
        } else if (endGoalAnswer === 'Complete Zero') {
            if (gender === 'Male' && pronouns === 'He / Him') {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">men ages ${ageRange}</span>, eliminating alcohol completely is the healthiest choice. We'll work together to get there at your pace.`;
            } else if (gender === 'Female' && pronouns === 'She / Her') {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">women ages ${ageRange}</span>, eliminating alcohol completely is the healthiest choice. We'll work together to get there at your pace.`;
            } else {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">people ages ${ageRange}</span>, eliminating alcohol completely is the healthiest choice. We'll work together to get there at your pace.`;
            }
            recommendedWeeklyDrinks = 0;
            recommendedDryDays = 7;
            note = 'We\'ll customize your plan to gradually reduce to your goal of zero drinks at a pace that feels realistic and comfortable for you. We\'ll provide the structure to get you there, on your terms!';
        } else {
            if (gender === 'Male' && pronouns === 'He / Him' && drinksAnswer >= 20) {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">men ages ${ageRange}</span>, here's what the National Institutes of Health recommend for target weekly consumption:`;
                recommendedWeeklyDrinks = '<14';
                recommendedDailyDrinks = '<4';
                recommendedDryDays = '4+';
                note = 'These are guidelines we\'ll use as a starting point, but you\'ll be able to customize your long-term goals along the way. You\'re in charge!';
            }
            else if (gender === 'Male' && pronouns === 'He / Him' && drinksAnswer < 20) {
                const highestDrinkingDay = drinkBaselines.reduce((a, b) => Math.max(a, b));
                const weeklyDrinks = Math.floor(drinksAnswer * 0.7);
                const dailyDrinks = highestDrinkingDay > 4 ? 4 : highestDrinkingDay - 1;
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">men ages ${ageRange}</span>, drinking less is always the healthiest choice. Here's the first set of targets we'll work towards together:`;
                recommendedWeeklyDrinks = `${weeklyDrinks === 0 ? '' : '<'}${weeklyDrinks}`;
                recommendedDailyDrinks = `${dailyDrinks === 0 ? '' : '<'}${dailyDrinks}`;
                recommendedDryDays = `${dryDaysAnswer < 4 ? 4 : dryDaysAnswer}${dryDaysAnswer === 7 ? '' : '+'}`;
                note = 'These are guidelines we\'ll use as a starting point, but you\'ll be able to customize your long-term goals along the way. You\'re in charge!';
            }
            else if (gender === 'Female' && pronouns === 'She / Her' && drinksAnswer >= 10) {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">women ages ${ageRange}</span>, here's what the National Institutes of Health recommend for target weekly consumption:`;
                recommendedWeeklyDrinks = '<8';
                recommendedDailyDrinks = '<2';
                recommendedDryDays = '4+';
                note = 'These are guidelines we\'ll use as a starting point, but you\'ll be able to customize your long-term goals along the way. You\'re in charge!';
            }
            else if (gender === 'Female' && pronouns === 'She / Her' && drinksAnswer < 10) {
                const weeklyDrinks = Math.floor(drinksAnswer * 0.7);
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">women ages ${ageRange}</span>, drinking less is always the healthiest choice. Here's the first set of targets we'll work towards together:`;
                recommendedWeeklyDrinks = `${weeklyDrinks === 0 ? '' : '<'}${weeklyDrinks}`;
                recommendedDailyDrinks = '<2';
                recommendedDryDays = `${dryDaysAnswer < 4 ? 4 : dryDaysAnswer}${dryDaysAnswer === 7 ? '' : '+'}`;
                note = 'These are guidelines we\'ll use as a starting point, but you\'ll be able to customize your long-term goals along the way. You\'re in charge!';
            }
            else if (drinksAnswer >= 10) {
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">people ages ${ageRange}</span>, here's what the National Institutes of Health recommend for target weekly consumption:`;
                recommendedWeeklyDrinks = '<8';
                recommendedDailyDrinks = '<2';
                recommendedDryDays = '4+';
                note = 'These are guidelines we\'ll use as a starting point, but you\'ll be able to customize your long-term goals along the way. You\'re in charge!';
            }
            else if (drinksAnswer < 10) {
                const weeklyDrinks = Math.floor(drinksAnswer * 0.7);
                body = `For <span style="color:${theme.palette.warning.main};font-weight:${TypographyWeights.BOLD};">people ages ${ageRange}</span>, drinking less is always the healthiest choice. Here's the first set of targets we'll work towards together:`;
                recommendedWeeklyDrinks = `${weeklyDrinks === 0 ? '' : '<'}${weeklyDrinks}`;
                recommendedDailyDrinks = '<2';
                recommendedDryDays = `${dryDaysAnswer < 4 ? 4 : dryDaysAnswer}${dryDaysAnswer === 7 ? '' : '+'}`;
                note = 'These are guidelines we\'ll use as a starting point, but you\'ll be able to customize your long-term goals along the way. You\'re in charge!';
            }
        }

        return {
            body,
            recommendedWeeklyDrinks,
            recommendedDailyDrinks,
            recommendedDryDays,
            note
        };
    }, [drinksAnswer, targetDrinksAnswer, targetDryDaysAnswer, endGoalAnswer, customTargetsAnswer, gender, pronouns, ageRange]);

    const {
        body, recommendedWeeklyDrinks, recommendedDailyDrinks, recommendedDryDays, note 
    } = calculateRecommendations();

    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 1500);
    });

    return (
        <CardContainer
            sectionKey={'aboutYou'}
            goBack={formContext.goBack}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'aboutYou'}
                />
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box className={classes.content}>
                            <Typography
                                variant={TypographyVariants.H3}
                                dangerouslySetInnerHTML={{
                                    __html: body 
                                }}
                            />
                            <Box className={classes.summary}>
                                <Box className={classes.item}>
                                    <Typography
                                        variant={TypographyVariants.H2}
                                        className={classes.value}
                                    >
                                        {recommendedWeeklyDrinks}
                                    </Typography>
                                    <Typography
                                        variant={TypographyVariants.H5}
                                        className={classes.text}
                                    >
                                        Drinks per Week
                                    </Typography>
                                </Box>
                                {recommendedDailyDrinks && (
                                    <Box className={classes.item}>
                                        <Typography
                                            variant={TypographyVariants.H2}
                                            className={classes.value}
                                        >
                                            {recommendedDailyDrinks}
                                        </Typography>
                                        <Typography
                                            variant={TypographyVariants.H5}
                                            className={classes.text}
                                        >
                                            Drinks per Day
                                        </Typography>
                                    </Box>
                                )}
                                <Box className={classes.item}>
                                    <Typography
                                        variant={TypographyVariants.H2}
                                        className={classes.value}
                                    >
                                        {recommendedDryDays}
                                    </Typography>
                                    <Typography
                                        variant={TypographyVariants.H5}
                                        className={classes.text}
                                    >
                                        Dry Days per week
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className={classes.tipBox}>
                                <Box className={classes.tipBodyBox}>
                                    <Typography
                                        variant={TypographyVariants.H6}
                                    >
                                        {note}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Slide>
                    <Box
                        style={{
                            height: '300px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default DrinkRecommendations;
